/** Dotted quad, each octet 0-255 with no leading zero (Python refuses `010.0.0.1`). */
export const isIpv4 = (v: string): boolean => {
	const parts = v.split('.')
	return (
		parts.length === 4 &&
		parts.every((p) => /^(0|[1-9]\d{0,2})$/.test(p) && Number(p) <= 255)
	)
}

const HEX_GROUP = /^[0-9a-f]{1,4}$/i

/**
 * Eight 16-bit groups, one `::` standing in for one or more zero groups, and an
 * optional dotted quad as the last two groups (`::ffff:10.0.0.1`).
 */
export const isIpv6 = (v: string): boolean => {
	const halves = v.split('::')
	if (halves.length > 2) return false

	const groupsOf = (half: string) => (half === '' ? [] : half.split(':'))
	const head = groupsOf(halves[0])
	const tail = halves.length === 2 ? groupsOf(halves[1]) : []
	// An embedded IPv4 only ever closes the address, and it fills two groups. "Closes"
	// means the last group written, so after a `::` it is the tail's: `1.2.3.4::` has
	// its quad before the `::`, and Python refuses it.
	const closing = halves.length === 2 ? tail : head
	const last = closing.at(-1)
	const quad = last?.includes('.') ? closing.pop() : undefined
	if (quad !== undefined && !isIpv4(quad)) return false

	const groups = [...head, ...tail]
	const count = groups.length + (quad === undefined ? 0 : 2)
	if (!groups.every((g) => HEX_GROUP.test(g))) return false

	return halves.length === 2 ? count <= 7 : count === 8
}

/**
 * IPv4 blocks with no public location: private, shared, loopback, link-local,
 * documentation, benchmarking, multicast and reserved space. GeoIP has nothing
 * for an address in one of them, so a blank location there is expected, not a
 * missing lookup. Each test reads the first three octets.
 */
const NON_PUBLIC_V4: ReadonlyArray<(o: number[]) => boolean> = [
	([a]) => a === 0 || a === 10 || a === 127 || a >= 224,
	([a, b]) => a === 100 && b >= 64 && b <= 127,
	([a, b]) => a === 169 && b === 254,
	([a, b]) => a === 172 && b >= 16 && b <= 31,
	([a, b]) => a === 192 && b === 168,
	([a, b, c]) => a === 192 && b === 0 && (c === 0 || c === 2),
	([a, b]) => a === 198 && (b === 18 || b === 19),
	([a, b, c]) => a === 198 && b === 51 && c === 100,
	([a, b, c]) => a === 203 && b === 0 && c === 113,
]

/**
 * Whether an address is private or reserved, so it has no public location —
 * the "Private or reserved IP" reason a blank geo cell gives. Mirrors the
 * blocks Python's `ipaddress` reads as not global, closely enough for a label.
 * A value that is not an address is not private: the caller says "Unknown
 * location" for it.
 */
export const isPrivateOrReservedIp = (ip: string): boolean => {
	const value = ip.trim().toLowerCase()
	if (isIpv4(value)) {
		const octets = value.split('.').map(Number)
		return NON_PUBLIC_V4.some((test) => test(octets))
	}
	if (!isIpv6(value)) return false
	const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(value)
	if (mapped) return isPrivateOrReservedIp(mapped[1])
	return (
		value === '::' ||
		value === '::1' ||
		/^f[cd]/.test(value) ||
		/^fe[89ab]/.test(value) ||
		value.startsWith('ff') ||
		value.startsWith('2001:db8:')
	)
}
